/**
 * @file: AchievementsCard.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * What the member has done lately, shaped by what they do: a service advisor
 * sees order and customer counts, a technician sees workshop counts, and
 * someone who is both sees all of them. A short list sits in one row; a
 * longer one pages sideways two rows at a time, so the card keeps one height
 * whatever the role.
 */

import { Typography } from '@euler/app/components/typography';
import { MeIcon, MeIconName } from '@euler/app/flows/me/components/MeIcon';
import { MK, MKTint } from '@euler/app/flows/me/components/meKit';
import { SectionCard } from '@euler/app/flows/me/components/SectionCard';
import { MyPerformanceState } from '@euler/app/flows/me/functions/useMyPerformance';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import {
  MemberPerformance,
  ServiceAgentPerformance,
  TechnicianPerformance,
} from '@euler/model/MemberPerformance';
import { splitEvery } from 'ramda';
import { memo, useState } from 'react';
import {
  ActivityIndicator,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  interpolate,
  interpolateColor,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

type Tone = keyof typeof MKTint;

type MetricStyle<K> = {
  key: K;
  label: string;
  icon: MeIconName;
  tone: Tone;
};

type Metric = MetricStyle<string> & { value: number };

const kServiceAgentMetrics: MetricStyle<
  Exclude<keyof ServiceAgentPerformance, 'daily'>
>[] = [
  { key: 'ordersPlaced', label: '开单', icon: 'cart-large-2', tone: 'brand' },
  {
    key: 'ordersFinished',
    label: '完工',
    icon: 'check-circle',
    tone: 'success',
  },
  {
    key: 'preInspections',
    label: '接车检查',
    icon: 'clipboard-check',
    tone: 'warn',
  },
  { key: 'deliveryChecks', label: '交车检查', icon: 'box', tone: 'purple' },
  { key: 'samplingChecks', label: '抽检', icon: 'test-tube', tone: 'danger' },
  {
    key: 'maintenancePlans',
    label: '保养方案',
    icon: 'calendar',
    tone: 'brand',
  },
  { key: 'leads', label: '线索', icon: 'user-plus', tone: 'brand' },
  { key: 'reservations', label: '预约', icon: 'bookmark', tone: 'brand' },
];

const kTechnicianMetrics: MetricStyle<keyof TechnicianPerformance>[] = [
  {
    key: 'faultsFound',
    label: '故障检出',
    icon: 'clipboard-check',
    tone: 'warn',
  },
  {
    key: 'constructionsDone',
    label: '施工完成',
    icon: 'wrench',
    tone: 'brand',
  },
  { key: 'reworksDone', label: '内返完成', icon: 'restart', tone: 'success' },
];

const metricsOf = ({ serviceAgent, technician }: MemberPerformance) => {
  const metrics: Metric[] = [];
  if (serviceAgent != null) {
    metrics.push(
      ...kServiceAgentMetrics.map(m => ({ ...m, value: serviceAgent[m.key] })),
    );
  }
  if (technician != null) {
    metrics.push(
      ...kTechnicianMetrics.map(m => ({ ...m, value: technician[m.key] })),
    );
  }
  return metrics;
};

export const AchievementsCard = memo(
  ({ state, onRetry }: { state: MyPerformanceState; onRetry: () => void }) => {
    const { rpx } = useResponsiveContext();
    const metrics = state.kind === 'ready' ? metricsOf(state.performance) : [];
    return (
      <SectionCard
        title="我的业绩"
        icon="cup-star"
        action={
          state.kind === 'ready' && (
            <Typography.Text size={rpx(12)} color={MK.mutedInk}>
              近{state.performance.days}天
            </Typography.Text>
          )
        }
      >
        <View style={{ marginTop: rpx(12) }}>
          {state.kind === 'ready' ? (
            metrics.length <= kSingleRowMax ? (
              <TileRow metrics={metrics} columns={metrics.length} />
            ) : (
              <PagedTiles metrics={metrics} />
            )
          ) : (
            <View
              style={{
                height: rpx(kStatusHeight),
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {state.kind === 'loading' ? (
                <ActivityIndicator color={MK.mutedInk} />
              ) : (
                <TouchableOpacity onPress={onRetry}>
                  <Typography.Text size={rpx(13)} color={MK.mutedInk}>
                    加载失败，点击重试
                  </Typography.Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      </SectionCard>
    );
  },
);

/** About one row of tiles, so the card barely moves when they arrive. */
const kStatusHeight = 88;

/** Up to this many metrics share a single row; more page two rows at a time. */
const kSingleRowMax = 3;
const kPageRows = 2;
/** Wide layouts have room for a third column, so fewer pages to swipe. */
const usePageColumns = () => (useIsDesktopLayout() ? 3 : 2);

const PagedTiles = memo(({ metrics }: { metrics: Metric[] }) => {
  const { rpx } = useResponsiveContext();
  // Pages must be exactly as wide as the viewport for paging to snap, and
  // that width is only known once laid out.
  const [width, setWidth] = useState(0);
  // Scroll position in pages (1.5 = halfway between the 2nd and 3rd), so
  // the dots can follow the finger rather than jump when a page settles.
  const progress = useSharedValue(0);
  const pageColumns = usePageColumns();
  const pages = splitEvery(pageColumns * kPageRows, metrics);
  const gap = rpx(kTileGap);
  const tileWidth = (width - gap * (pageColumns - 1)) / pageColumns;
  // Each page carries a trailing gap so pages don't butt together while
  // swiping. The viewport is widened by that gap (into the card's padding)
  // so it still snaps to whole pages and rests flush with the content edge.
  const pageWidth = width + gap;

  const onLayout = (e: LayoutChangeEvent) =>
    setWidth(e.nativeEvent.layout.width);
  // A plain JS scroll handler rather than `useAnimatedScrollHandler`, which
  // doesn't receive scroll events on web; the dots still animate from the
  // shared value on every platform.
  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (pageWidth > 0) {
      progress.value = e.nativeEvent.contentOffset.x / pageWidth;
    }
  };

  return (
    <View onLayout={onLayout}>
      {width > 0 && (
        <ScrollView
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={onScroll}
          style={{ marginRight: -gap }}
        >
          {pages.map((pageMetrics, i) => (
            <View key={i} style={{ width: pageWidth, paddingRight: gap, gap }}>
              {splitEvery(pageColumns, pageMetrics).map((row, j) => (
                <TileRow
                  key={j}
                  metrics={row}
                  columns={pageColumns}
                  tileWidth={tileWidth}
                />
              ))}
            </View>
          ))}
        </ScrollView>
      )}
      {pages.length > 1 && (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            gap: rpx(6),
            marginTop: rpx(12),
          }}
        >
          {pages.map((_, i) => (
            <PageDot key={i} index={i} progress={progress} />
          ))}
        </View>
      )}
    </View>
  );
});

/**
 * A page dot that stretches into a pill as its page scrolls into view. Width
 * and colour track the scroll position, so swiping morphs the active pill
 * from one dot to the next instead of snapping.
 */
const PageDot = memo(
  ({ index, progress }: { index: number; progress: SharedValue<number> }) => {
    const { rpx } = useResponsiveContext();
    const dot = rpx(6);
    const pill = rpx(18);
    const style = useAnimatedStyle(() => {
      // 0 when this page is in view, 1 once a whole page away.
      const distance = Math.min(Math.abs(progress.value - index), 1);
      return {
        width: interpolate(distance, [0, 1], [pill, dot]),
        backgroundColor: interpolateColor(
          distance,
          [0, 1],
          [MK.brand, MK.hairline],
        ),
      };
    });
    return (
      <Animated.View style={[{ height: dot, borderRadius: dot / 2 }, style]} />
    );
  },
);

const kTileGap = 10;

/**
 * One row of tinted tiles. Given `tileWidth`, every tile takes exactly that
 * width, so a short last row lines up with the full rows above it; flex
 * alone doesn't guarantee that on web, where items get a content minimum.
 */
const TileRow = memo(
  ({
    metrics,
    columns,
    tileWidth,
  }: {
    metrics: Metric[];
    columns: number;
    tileWidth?: number;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ flexDirection: 'row', gap: rpx(kTileGap) }}>
        {metrics.map(metric => (
          <MetricTile key={metric.key} metric={metric} width={tileWidth} />
        ))}
        {tileWidth == null &&
          Array.from({ length: columns - metrics.length }, (_, i) => (
            <View key={`pad-${i}`} style={{ flex: 1, minWidth: 0 }} />
          ))}
      </View>
    );
  },
);

const MetricTile = memo(
  ({ metric, width }: { metric: Metric; width?: number }) => {
    const { rpx } = useResponsiveContext();
    const color = MK[metric.tone];
    return (
      <View
        style={{
          ...(width != null ? { width } : { flex: 1, minWidth: 0 }),
          borderRadius: rpx(12),
          backgroundColor: MKTint[metric.tone],
          padding: rpx(12),
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              width: rpx(32),
              height: rpx(32),
              borderRadius: rpx(16),
              backgroundColor: color,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MeIcon name={metric.icon} size={rpx(16)} color={MK.onBrand} />
          </View>
          <Typography.Text
            size={rpx(24)}
            lineHeight={rpx(30)}
            color={color}
            bold
          >
            {metric.value}
          </Typography.Text>
        </View>
        <Typography.Text
          size={rpx(12)}
          color={MK.ink}
          medium
          mt={rpx(8)}
          numberOfLines={1}
        >
          {metric.label}
        </Typography.Text>
      </View>
    );
  },
);
